import React, { useRef, useState } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Check, Mail } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import { getSupabaseClient } from '../lib/supabaseClient'
import { notifyLeadByEmail } from '../lib/notifyLead'

const ease = [0.16, 1, 0.3, 1] as const

const colors = {
  teal: '#145F6B',
  tealDark: '#083B3A',
  copper: '#9B775D',
  nearBlack: '#0F0F0F',
}

// ============================================
// COMING SOON – single section, branded, waitlist
// ============================================
const AppPage: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const supabase = getSupabaseClient()
      const { error } = await supabase.from('leads').insert({
        source: 'app_waitlist',
        email: email.trim(),
        full_name: null,
        phone: null,
        payload: {},
      })
      if (error) throw error
      await notifyLeadByEmail({
        source: 'app_waitlist',
        email: email.trim(),
        full_name: null,
        phone: null,
        payload: {},
      })
      setSubmitted(true)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <section ref={ref} className="relative min-h-screen overflow-hidden bg-[#0b1a1a]">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a1515] via-[#0d1e1e] to-[#081212]" />
            <div
              className="absolute inset-0 opacity-20"
              style={{ background: `radial-gradient(ellipse 80% 50% at 50% 100%, ${colors.tealDark}80, transparent)` }}
            />
          </div>

          <motion.div style={{ opacity }} className="relative w-full min-h-screen flex items-center justify-center pt-24 pb-20">
            <div className="max-w-[720px] mx-auto px-6 lg:px-12 w-full text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="inline-flex items-center gap-3 px-4 py-2 bg-white/10 backdrop-blur-sm mb-8"
              >
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[13px] text-white/80">Coming Soon</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease, delay: 0.3 }}
                className="font-serif text-white mb-6"
                style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
              >
                The Hudson My Home™ app is on its way.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-xl lg:text-2xl leading-relaxed text-white/60 max-w-lg mx-auto mb-10"
              >
                We're building something that will make it easier than ever to care for your home.
                Join the waitlist and we'll let you know as soon as it's ready.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1 relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          required
                          disabled={submitting}
                          className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40 transition-colors disabled:opacity-70"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="group flex items-center justify-center gap-2 bg-white text-grey-900 px-6 py-4 font-medium transition-all duration-300 hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {submitting ? 'Submitting…' : 'Join Waitlist'}
                        {!submitting && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                      </button>
                    </div>
                    {submitError && <p className="mt-3 text-sm text-red-300">{submitError}</p>}
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md mx-auto p-6 bg-white/10 border border-white/20"
                  >
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium">You're on the list</p>
                        <p className="text-white/50 text-sm">We'll notify you when the app launches.</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-12 text-white/40 text-sm"
              >
                In the meantime, explore our{' '}
                <Link to="/membership" className="underline hover:text-white/60" style={{ color: colors.copper }}>
                  home membership
                </Link>{' '}
                for full-service care today.
              </motion.p>
            </div>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default AppPage
